/**
 * D212: a fuzzy correction into the clause's HEAD VERB is a question, not an
 * act.
 *
 * @remarks
 * Calder's finding (2026-09-25): `wake the drum` — `wake` a real English
 * word in no vocabulary — was corrected one letter into `take the drum` and
 * EXECUTED. Mike's ruling: the pack's prose is its dictionary (a word the
 * pack speaks anywhere is never a typo — the consumer passes those words as
 * `knownWords`, and the rung leaves them alone), and for a word the prose
 * does not contain, a correction into a head verb ASKS — "Did you mean TAKE
 * THE DRUM?" — while noun-position corrections keep executing, because the
 * entity a corrected noun resolves to is the check that the correction was
 * right, and a verb has no such check.
 *
 * Judged on the FINISHED clause rather than inside the correction pass, so a
 * D18 near-tie walk (`opne gaet`: `gaet` is one edit from both `gate` and
 * `get`) settles its tie by parsing first and the one question put to the
 * player is about the reading that survived — `open gate` — not about each
 * candidate in turn. The parser does not ask questions itself; it reports.
 * This is the report: a `verb_corrected` diagnostic carrying the clause as
 * the rung would have read it, every correction applied, for the consumer
 * to put to the player.
 *
 * @packageDocumentation
 */

// cspell:ignore opne gaet -- the deliberate typos the remark above walks

import { type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { type ClauseParseResult } from '../rescue/parse-guided-rescues.js';
import { type ParsedCommand, type TokenCorrection } from '../types.js';

/**
 * The clause with every correction written in, spans applied right to left
 * so earlier offsets stay true.
 *
 * @param clauseText - The clause, folded, as typed
 * @param corrections - The corrections the reading applied
 * @returns The clause as the rung read it
 */
const applyCorrections = (clauseText: string, corrections: readonly TokenCorrection[]): string =>
	[...corrections]
		.sort((left, right) => right.sourceSpan.start - left.sourceSpan.start)
		.reduce(
			(text, correction) => `${text.slice(0, correction.sourceSpan.start)}${correction.correctedText}${text.slice(correction.sourceSpan.end)}`,
			clauseText,
		);

/**
 * Turn a parsed clause whose intent came from a fuzzy correction into the
 * `verb_corrected` report; pass every other result through untouched.
 *
 * @param clauseText - The clause, folded, as the player typed it
 * @param result - The clause's finished result
 * @param lexicon - The compiled vocabulary (which words are verbs)
 * @returns The result, or the report in its place
 */
export const askAboutHeadVerbCorrection = (
	clauseText: string,
	result: ClauseParseResult,
	lexicon: Pick<CompiledLexicon, 'intentsByToken'>,
): ClauseParseResult => {
	if (result.type !== 'command') {
		return result;
	}

	const verbCorrection = headVerbCorrectionOf(result.command, lexicon);

	if (verbCorrection === undefined) {
		return result;
	}

	return {
		diagnostics: {
			correctedClauseText: applyCorrections(clauseText, result.command.corrections),
			failureKind: 'verb_corrected',
			understoodIntentToken: result.command.intent,
			unmatchedTokens: [verbCorrection.originalText],
		},
		type: 'diagnostics',
	};
};

/**
 * The correction that supplied the command's verb, if any: a corrected word
 * the vocabulary knows as the very intent the command carries. A word
 * corrected INTO a verb's spelling in noun position (D21's demoted `pry`)
 * carries a different intent, or none, and is left to the resolver.
 *
 * @param command - The parsed command
 * @param lexicon - The compiled vocabulary
 * @returns The head verb's correction, or nothing
 */
const headVerbCorrectionOf = (command: ParsedCommand, lexicon: Pick<CompiledLexicon, 'intentsByToken'>): TokenCorrection | undefined =>
	command.corrections.find((correction) => lexicon.intentsByToken.get(correction.correctedText)?.canonicalToken === command.intent);
